import React, { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { getAllAdminUsers, getPracticeManagementVersionInfo } from "services";

import { DEFAULT_CACHE, GET_ADMIN_USERS, PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_VERSION_INFO } from "consts";

import { Button, Flex, Form, Modal, Option, Select, TextArea } from "components";

import { ChangesSummary } from "pages/admin/practiceManagementService/ChangesSummary";
import { usePracticeManagementDiff } from "pages/admin/practiceManagementService/usePracticeManagementDiff";

type PublishFormValues = {
  description: string;
  reason: string;
  impactAssessment: string;
  proposedBy: string[];
  assessedBy: string[];
  decisionMadeBy: string[];
  references: string;
};

type PublishButtonProps = {
  onPublish: (formValues: PublishFormValues) => Promise<void>;
  isDraftVersion: boolean;
  selectedVersion: string | undefined;
  versions: string[];
  canEdit: boolean;
  className?: string;
};

export const PublishButton = ({ onPublish, isDraftVersion, selectedVersion, versions, canEdit, className }: PublishButtonProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"summary" | "form">("summary");
  const [form] = Form.useForm<PublishFormValues>();

  const latestPublishedVersion = useMemo(() => versions.filter((v) => v !== "draft")[0], [versions]);
  const { isLoadingDiff, endpointSummaries } = usePracticeManagementDiff(latestPublishedVersion, "draft", open && isDraftVersion);

  const { data: adminUsers = [] } = useQuery({
    queryKey: [GET_ADMIN_USERS],
    queryFn: () => getAllAdminUsers({ verifier: null }),
    ...DEFAULT_CACHE,
  });

  const { data: versionInfo } = useQuery({
    queryKey: [PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_VERSION_INFO, selectedVersion],
    queryFn: () => getPracticeManagementVersionInfo(selectedVersion!),
    enabled: open && !isDraftVersion && !!selectedVersion,
  });

  useEffect(() => {
    if (open && !isDraftVersion && versionInfo) {
      form.setFieldsValue({
        ...versionInfo,
        proposedBy: versionInfo.proposedBy ? versionInfo.proposedBy.split(", ") : [],
        assessedBy: versionInfo.assessedBy ? versionInfo.assessedBy.split(", ") : [],
        decisionMadeBy: versionInfo.decisionMadeBy ? versionInfo.decisionMadeBy.split(", ") : [],
      });
    }
  }, [versionInfo, isDraftVersion, form, open]);

  const handleOpen = () => {
    setStep(isDraftVersion && !!latestPublishedVersion ? "summary" : "form");
    setOpen(true);
  };

  const handleFinish = async (values: PublishFormValues) => {
    try {
      await onPublish(values);
      setOpen(false);
      form.resetFields();
    } catch {
      // error message handled by mutation's onError
    }
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleOk = () => {
    if (step === "summary") {
      setStep("form");
    } else {
      form.submit();
    }
  };

  return (
    <>
      <Button type="primary" className={className} disabled={isDraftVersion && !canEdit} onClick={handleOpen}>
        {isDraftVersion ? "Publish draft" : "Show version info"}
      </Button>
      <Modal
        showModal={open}
        title={step === "summary" ? "Review Changes" : isDraftVersion ? "Publish" : `Version ${selectedVersion}`}
        okText={step === "summary" ? "Continue" : "Publish"}
        onOk={isDraftVersion ? handleOk : undefined}
        onCancel={handleCancel}
        footer={isDraftVersion ? undefined : null}
        wrapClassName="pms-publish-modal"
        content={
          step === "summary" ? (
            <ChangesSummary isLoading={isLoadingDiff} endpointSummaries={endpointSummaries} fromVersion="draft" toVersion={latestPublishedVersion} />
          ) : (
            <Form form={form} onFinish={handleFinish}>
              <Flex vertical gap={16} className="margin-50">
                <TextArea
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="description"
                  label="Description"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  rows={1}
                  autoSize
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-description"
                />
                <TextArea
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="reason"
                  label="Reason"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  rows={1}
                  autoSize
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-reason"
                />
                <TextArea
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="impactAssessment"
                  label="Impact assessment"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  rows={1}
                  autoSize
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-impact-assessment"
                />
                <Select
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="proposedBy"
                  label="Proposed by"
                  mode="multiple"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  showSearch
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-proposed-by"
                >
                  {adminUsers.map((user) => (
                    <Option key={user.id} value={user.name}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
                <Select
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="assessedBy"
                  label="Assessed by"
                  mode="multiple"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  showSearch
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-assessed-by"
                >
                  {adminUsers.map((user) => (
                    <Option key={user.id} value={user.name}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
                <Select
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="decisionMadeBy"
                  label="Decision made by"
                  mode="multiple"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  showSearch
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-decision-made-by"
                >
                  {adminUsers.map((user) => (
                    <Option key={user.id} value={user.name}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
                <TextArea
                  className={`${!isDraftVersion ? "floating-label active" : ""}`}
                  name="references"
                  label="References"
                  rules={isDraftVersion ? [{ required: true }] : []}
                  rows={1}
                  autoSize
                  disabled={!isDraftVersion}
                  dataTestId="pms-publish-references"
                />
              </Flex>
            </Form>
          )
        }
      />
    </>
  );
};
